import { compareInstantsAtMicrosecondPrecision, normalizeInstantToUtcMicroseconds } from "../lib/temporalInstant";
import { apiClient } from "./client";

export const GROUNDED_PROVENANCE_CONTRACT_VERSION = "thread.grounded-provenance.v1" as const;
export const GROUNDED_PROVENANCE_RESOURCE_LIMITS_VERSION = "thread.grounded-provenance-resource-limits.v1" as const;
export const GROUNDED_PROVENANCE_JSON_MEDIA_TYPE = "application/vnd.thread.grounded-provenance.v1+json" as const;
export const GROUNDED_PROVENANCE_PATH = "/thread/v1/provenance/interrogate" as const;
export const GROUNDED_PROVENANCE_MIN_SCHEMA_MIGRATION = "20260811120000" as const;

const MAX_QUESTION_LENGTH = 500;
const MAX_DOCUMENT_CANONICAL_BYTES = 2_097_152;
const MAX_CITATIONS = 500;
const MAX_PATHS = 50;
const MAX_PATH_STEPS = 500;
const MAX_TOOL_CALLS = 2;
const MAX_STATEMENTS = 8;

const SOURCE_CONTRACTS = {
  proof_trace: "thread.proof-trace.v2",
  impact_analysis: "thread.impact.v2",
  temporal_compare: "thread.lineage.temporal-compare.v1",
  human_provenance: "thread.human-provenance.v1",
} as const;

const PROOF_CITATION_ANCHOR_KINDS = new Set([
  "artifact", "artifact_version", "operation_spec", "operation_run", "tool", "actor", "binding",
  "binding_state_event", "operation_state_event", "supersession", "merge_input", "lifecycle_event",
  "decision", "decision_status_event", "decision_evidence", "operation_registration_receipt",
  "artifact_invalidation_receipt",
]);
const PROOF_ASSERTION_ANCHOR_KINDS = new Set([
  "artifact_identity_assertion", "artifact_version_identity_assertion", "artifact_value_assertion",
  "content_identity_assertion", "binding_semantics_assertion", "operation_spec_definition_assertion",
  "operation_invocation_assertion", "operation_parameters_assertion", "operation_environment_assertion",
  "operation_assumptions_assertion", "tool_snapshot_assertion", "actor_identity_assertion",
  "lifecycle_validity_assertion", "physical_semantics_references_assertion", "engineering_decision_assertion",
  "decision_evidence_assertion", "supersession_assertion", "merge_selection_assertion",
  "state_transition_assertion", "receipt_attestation_assertion",
]);
const TEMPORAL_CITATION_ANCHOR_KINDS = new Set([
  "artifact_version", "operation_run", "binding", "supersession", "lifecycle_event", "decision",
  "decision_evidence", "decision_status_event", "topology_retirement",
]);
const HUMAN_ITEM_ANCHOR_KINDS = new Set(["decision", "decision_evidence", "journal_entry"]);
const WHY_CONCLUSION_TEMPLATE = /^At this authenticated snapshot, the canonical proof (?:marks|does not mark) the selected ArtifactVersion as valid\.$/;
const WHY_SCOPE_TEMPLATE = /^The canonical proof contains (?:0|[1-9]\d*) authorized visible proof paths? at this snapshot\.$/;
const WHY_CONTEXT_TEMPLATE = /^This page contains (?:0|[1-9]\d*) authorized decision, evidence, or journal records? related to the selected value\.$/;
const IMPACT_CONCLUSION_TEMPLATE = /^Changing the selected reference has (?:0|[1-9]\d*) authorized downstream ArtifactVersions? on invalidity-propagating paths\.$/;
const IMPACT_SCOPE_TEMPLATE = /^The typed impact result reports (?:0|[1-9]\d*) stopped and (?:0|[1-9]\d*) indeterminate subjects?\.$/;
const TEMPORAL_CONCLUSION_TEMPLATE = /^The canonical comparison reports (?:0|[1-9]\d*) visible typed changes? across the requested window\.$/;
const TEMPORAL_SCOPE_TEMPLATE = /^Topology added (?:0|[1-9]\d*), topology removed (?:0|[1-9]\d*), validity changed (?:0|[1-9]\d*), and decision\/evidence changes total (?:0|[1-9]\d*)\.$/;
const DECISION_CONTEXT_TEMPLATE = /^This authorized temporal page contains (?:0|[1-9]\d*) related decision, evidence, or journal records?\.$/;
const NON_EVIDENCE_TEMPLATE = /^(?:No supported provenance question matched\. Use a deterministic Lineage command or ask about validity, change, downstream invalidity, or decision evidence\.|The question matches more than one provenance analysis\.|An exact ArtifactVersion must be selected first\.|Temporal comparison requires an ordered before\/after window\.)$/;
const STATEMENT_TEMPLATES = [
  WHY_CONCLUSION_TEMPLATE, WHY_SCOPE_TEMPLATE, WHY_CONTEXT_TEMPLATE,
  IMPACT_CONCLUSION_TEMPLATE, IMPACT_SCOPE_TEMPLATE,
  TEMPORAL_CONCLUSION_TEMPLATE, TEMPORAL_SCOPE_TEMPLATE,
  DECISION_CONTEXT_TEMPLATE, NON_EVIDENCE_TEMPLATE,
];

export type GroundedProvenanceIntent =
  | "why_valid"
  | "what_changed"
  | "downstream_invalidation"
  | "decision_basis";

export type GroundedProvenanceStatus =
  | "answered"
  | "incomplete"
  | "redacted"
  | "no_match"
  | "ambiguous"
  | "needs_context";

export type GroundedProvenanceSourceTool =
  | "proof_trace"
  | "impact_analysis"
  | "temporal_compare"
  | "human_provenance";

export interface GroundedArtifactVersionRef {
  entity_kind: "artifact_version";
  id: string;
}

export interface GroundedComparisonWindow {
  before_as_of: string;
  after_as_of: string;
}

export interface GroundedProvenanceQuestionRequest {
  project_id: string;
  question: string;
  root_ref?: GroundedArtifactVersionRef | null;
  as_of?: string | null;
  recorded_as_of?: string | null;
  snapshot_token?: string | null;
  comparison?: GroundedComparisonWindow | null;
}

export interface GroundedPointSnapshot {
  snapshot_kind: "point";
  effective_as_of: string;
  recorded_as_of: string;
  snapshot_token: string;
}

export interface GroundedComparisonSnapshot {
  snapshot_kind: "comparison";
  before_as_of: string;
  after_as_of: string;
  recorded_as_of: string;
  snapshot_token: string;
}

export type GroundedTemporalSnapshot = GroundedPointSnapshot | GroundedComparisonSnapshot;

export interface GroundedProofTraceSourceRequest {
  source_request_kind: "proof_trace";
  project_id: string;
  root_ref: GroundedArtifactVersionRef;
  as_of: string;
  recorded_as_of: string;
  snapshot_token: string;
  max_depth: 32;
  max_records: 500;
  max_paths: 5000;
  max_path_steps: 5000;
}

export interface GroundedImpactSourceRequest {
  source_request_kind: "impact_analysis";
  project_id: string;
  root_ref: GroundedArtifactVersionRef;
  as_of: string;
  recorded_as_of: string;
  snapshot_token: string;
  max_depth: 32;
  max_records: 500;
  max_paths: 5000;
  max_path_steps: 5000;
}

export interface GroundedTemporalCompareSourceRequest {
  source_request_kind: "temporal_compare";
  project_id: string;
  root_refs: [GroundedArtifactVersionRef];
  before_as_of: string;
  after_as_of: string;
  recorded_as_of: string;
  snapshot_token: string;
  direction: "both";
  max_depth: 32;
  max_records: 500;
  max_paths: 5000;
  max_path_steps: 10000;
}

export interface GroundedHumanSourceFilters {
  actor_ids: [];
  decision_statuses: [];
  evidence_types: [];
  entry_types: [];
  sensitivity_tier_ids: [];
  lifecycle_states: [];
  effective_from: null;
  effective_through: null;
}

export interface GroundedHumanSourceRequest {
  source_request_kind: "human_provenance";
  project_id: string;
  projection: "related";
  root_ref: GroundedArtifactVersionRef;
  as_of: string;
  recorded_as_of: string;
  snapshot_token: string;
  record_kinds: ["decision", "decision_evidence", "journal_entry"];
  query: null;
  filters: GroundedHumanSourceFilters;
  group_by: null;
  sort: "effective_at_desc";
  page_size: 50;
  cursor: null;
}

export type GroundedSourceRequest =
  | GroundedProofTraceSourceRequest
  | GroundedImpactSourceRequest
  | GroundedTemporalCompareSourceRequest
  | GroundedHumanSourceRequest;

export interface GroundedToolCall {
  ordinal: number;
  tool: GroundedProvenanceSourceTool;
  source_contract_version: string;
  source_semantic_digest: string;
  source_request: GroundedSourceRequest;
  evidence_status: "complete" | "incomplete" | "redacted";
  visible_record_count: number;
}

export interface GroundedCitation {
  citation_id: string;
  source_tool: GroundedProvenanceSourceTool;
  source_contract_version: string;
  source_semantic_digest: string;
  source_locator: string;
  record_kind: string;
  record_id: string;
  secondary_record_id: string | null;
  effective_at: string | null;
  recorded_at: string | null;
}

export interface GroundedCitationStep {
  step_kind: "citation";
  ordinal: number;
  role: string;
  citation_ids: string[];
}

export interface GroundedOperationStep {
  step_kind: "operation";
  ordinal: number;
  from_artifact_version_id: string;
  input_binding_id: string;
  operation_id: string;
  output_binding_id: string;
  to_artifact_version_id: string;
  citation_ids: string[];
}

export interface GroundedSupersessionStep {
  step_kind: "supersession";
  ordinal: number;
  from_artifact_version_id: string;
  supersession_id: string;
  to_artifact_version_id: string;
  citation_ids: string[];
}

export type GroundedPathStep = GroundedCitationStep | GroundedOperationStep | GroundedSupersessionStep;

export interface GroundedPath {
  path_id: string;
  source_tool: "proof_trace" | "impact_analysis" | "temporal_compare";
  source_path_id: string;
  steps: GroundedPathStep[];
  terminal_kind: string;
  terminal_reason: string;
}

export interface GroundedStatement {
  statement_id: string;
  statement_kind:
    | "conclusion"
    | "scope"
    | "completeness"
    | "decision_context"
    | "no_match"
    | "clarification";
  text: string;
  citation_ids: string[];
}

export interface GroundedRequirement {
  code: "artifact_version_root" | "comparison_window" | "clarify_intent";
  message: string;
}

export interface GroundedWarning {
  code:
    | "proof_incomplete"
    | "impact_incomplete"
    | "temporal_history_incomplete"
    | "restricted_evidence_omitted"
    | "human_history_incomplete"
    | "citation_limit_reached"
    | "path_limit_reached"
    | "no_authorized_human_evidence"
    | "human_evidence_page_truncated";
}

export interface GroundedOpenAction {
  action_id: string;
  target_view: "proof_trace" | "impact" | "temporal_compare" | "human_provenance";
  label: string;
  tool_call_ordinal: number;
  root_ref: GroundedArtifactVersionRef;
  highlight_citation_id: string | null;
}

export interface GroundedProvenanceDocument {
  contract_version: typeof GROUNDED_PROVENANCE_CONTRACT_VERSION;
  resource_limits_version: typeof GROUNDED_PROVENANCE_RESOURCE_LIMITS_VERSION;
  project_id: string;
  question: string;
  normalized_question: string;
  intent: GroundedProvenanceIntent | null;
  status: GroundedProvenanceStatus;
  root_ref: GroundedArtifactVersionRef | null;
  temporal_snapshot: GroundedTemporalSnapshot | null;
  statements: GroundedStatement[];
  citations: GroundedCitation[];
  paths: GroundedPath[];
  tool_calls: GroundedToolCall[];
  open_actions: GroundedOpenAction[];
  requirements: GroundedRequirement[];
  warnings: GroundedWarning[];
}

export interface GroundedProvenanceResult {
  document: GroundedProvenanceDocument;
  semantic_identity: {
    digest: string;
    canonicalizer: "rfc8785";
    canonicalizer_version: "1";
    byte_length: number;
    media_type: typeof GROUNDED_PROVENANCE_JSON_MEDIA_TYPE;
  };
}

export interface GroundedProvenanceEnvelope {
  schema_version: "thread.api.v1";
  request_id: string;
  data: GroundedProvenanceResult;
  page: null;
  provenance: {
    source: "thread_core";
    projection_version: typeof GROUNDED_PROVENANCE_CONTRACT_VERSION;
    schema_migration: string;
  };
  warnings: [];
}

type UnknownRecord = Record<string, unknown>;

const REQUEST_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const DIGEST = /^sha256:[0-9a-f]{64}$/;
const SNAPSHOT_TOKEN = /^ts1\.[0-9a-f]{16}\.[A-Za-z0-9_-]{39,4060}$/;
const EXACT_UTC = /^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d\.\d{6}Z$/;
const AWARE_INSTANT = /^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;
const IDENTIFIER = /^[a-z][a-z0-9_.:-]{0,199}$/;
const NO_CONTROL = /^[^\x00-\x1f\x7f]*$/;

export class GroundedProvenanceValidationError extends Error {
  readonly path: string;

  constructor(path: string, expectation: string) {
    super(`${path}: ${expectation}`);
    this.name = "GroundedProvenanceValidationError";
    this.path = path;
  }
}

function fail(path: string, expectation: string): never {
  throw new GroundedProvenanceValidationError(path, expectation);
}

function record(value: unknown, path: string): UnknownRecord {
  if (value === null || typeof value !== "object" || Array.isArray(value)) fail(path, "expected an object");
  return value as UnknownRecord;
}

function exactRecord(
  value: unknown,
  path: string,
  required: readonly string[],
  optional: readonly string[] = [],
): UnknownRecord {
  const item = record(value, path);
  const allowed = new Set([...required, ...optional]);
  for (const key of Object.keys(item)) if (!allowed.has(key)) fail(`${path}.${key}`, "unexpected property");
  for (const key of required) if (!Object.prototype.hasOwnProperty.call(item, key)) fail(`${path}.${key}`, "missing required property");
  return item;
}

function array(value: unknown, path: string, maximum: number, minimum = 0): unknown[] {
  if (!Array.isArray(value)) fail(path, "expected an array");
  if (value.length < minimum || value.length > maximum) fail(path, `expected ${minimum}..${maximum} entries`);
  return value;
}

function text(value: unknown, path: string, maximum: number, minimum = 1): string {
  if (typeof value !== "string" || [...value].length < minimum || [...value].length > maximum) {
    fail(path, `expected a ${minimum}..${maximum} character string`);
  }
  if (!NO_CONTROL.test(value)) fail(path, "must not contain control characters");
  return value;
}

function literal<T extends string>(value: unknown, path: string, allowed: readonly T[]): T {
  if (typeof value !== "string" || !allowed.includes(value as T)) fail(path, `expected one of ${allowed.join(", ")}`);
  return value as T;
}

function integer(value: unknown, path: string, minimum: number, maximum: number): number {
  if (!Number.isSafeInteger(value) || (value as number) < minimum || (value as number) > maximum) {
    fail(path, `expected an integer from ${minimum} through ${maximum}`);
  }
  return value as number;
}

function requestUuid(value: unknown, path: string): string {
  const result = text(value, path, 36, 36);
  if (!REQUEST_UUID.test(result)) fail(path, "expected a UUID");
  return result;
}

function uuid(value: unknown, path: string): string {
  const result = text(value, path, 36, 36);
  if (!UUID.test(result)) fail(path, "expected a canonical lowercase UUID");
  return result;
}

function nullableUuid(value: unknown, path: string): string | null {
  return value === null ? null : uuid(value, path);
}

function digest(value: unknown, path: string): string {
  const result = text(value, path, 71, 71);
  if (!DIGEST.test(result)) fail(path, "expected a lowercase SHA-256 digest");
  return result;
}

function validInstant(value: string): boolean {
  const calendar = /^(\d{4})-(\d{2})-(\d{2})T/.exec(value);
  if (!calendar) return false;
  const year = Number(calendar[1]);
  const month = Number(calendar[2]);
  const day = Number(calendar[3]);
  if (year < 1 || year > 9999 || month < 1 || month > 12) return false;
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1]!;
  if (day < 1 || day > daysInMonth) return false;
  const offset = /([+-])(\d{2}):(\d{2})$/.exec(value);
  if (offset && (Number(offset[2]) > 23 || Number(offset[3]) > 59)) return false;
  const milliseconds = Date.parse(value);
  if (!Number.isFinite(milliseconds)) return false;
  return true;
}

function exactUtc(value: unknown, path: string): string {
  const result = text(value, path, 27, 27);
  if (!EXACT_UTC.test(result) || !validInstant(result)) fail(path, "expected an exact UTC instant with six fractional digits");
  return result;
}

function awareInstant(value: unknown, path: string): string {
  const result = text(value, path, 64);
  if (!AWARE_INSTANT.test(result) || !validInstant(result)) fail(path, "expected an aware RFC 3339 instant");
  return result;
}

function snapshotToken(value: unknown, path: string): string {
  const result = text(value, path, 4096, 60);
  if (!SNAPSHOT_TOKEN.test(result)) fail(path, "expected a bounded ts1 snapshot token");
  return result;
}

function identifier(value: unknown, path: string): string {
  const result = text(value, path, 200);
  if (!IDENTIFIER.test(result)) fail(path, "expected a canonical identifier");
  return result;
}

function artifactRef(value: unknown, path: string, request = false): GroundedArtifactVersionRef {
  const item = exactRecord(value, path, ["entity_kind", "id"]);
  literal(item.entity_kind, `${path}.entity_kind`, ["artifact_version"] as const);
  (request ? requestUuid : uuid)(item.id, `${path}.id`);
  return value as GroundedArtifactVersionRef;
}

function nullableArtifactRef(value: unknown, path: string): GroundedArtifactVersionRef | null {
  return value === null ? null : artifactRef(value, path);
}

function compare(left: string, right: string): number {
  return compareInstantsAtMicrosecondPrecision(left, right);
}

function sortedUnique(values: readonly string[]): boolean {
  return values.every((value, index) => index === 0 || values[index - 1]! < value);
}

function sameArtifactRef(left: GroundedArtifactVersionRef, right: GroundedArtifactVersionRef): boolean {
  return left.entity_kind === right.entity_kind && left.id === right.id;
}

function digestList(value: unknown, path: string, maximum: number, minimum = 0): string[] {
  const result = array(value, path, maximum, minimum).map((entry, index) => digest(entry, `${path}[${index}]`));
  if (!sortedUnique(result)) fail(path, "must contain unique digests in lexical order");
  return result;
}

/**
 * Python uses Unicode casefold after NFKC. Browsers expose lowercase but not
 * the versioned Unicode casefold table, so this client admits the NFKC ASCII
 * subset only. That makes normalization identical across runtimes instead of
 * silently misbinding a question under locale- or engine-specific casing.
 */
export function normalizeGroundedQuestion(value: string, path = "question"): string {
  const compatible = value.normalize("NFKC");
  if ([...compatible].some((character) => character.charCodeAt(0) > 0x7e)) {
    fail(path, "web grounded questions must normalize to the ASCII parity-safe subset");
  }
  return compatible.trim().toLowerCase().split(/\s+/).filter(Boolean).join(" ");
}

export function validateGroundedProvenanceRequest(
  value: unknown,
  path = "request",
): asserts value is GroundedProvenanceQuestionRequest {
  const item = exactRecord(
    value,
    path,
    ["project_id", "question"],
    ["root_ref", "as_of", "recorded_as_of", "snapshot_token", "comparison"],
  );
  requestUuid(item.project_id, `${path}.project_id`);
  const question = text(item.question, `${path}.question`, MAX_QUESTION_LENGTH);
  if (question !== question.trim()) fail(`${path}.question`, "must be trimmed");
  const normalizedQuestion = normalizeGroundedQuestion(question, `${path}.question`);
  if ([...normalizedQuestion].length > MAX_QUESTION_LENGTH) {
    fail(`${path}.question`, "normalized question exceeds its length limit");
  }
  if (item.root_ref !== undefined && item.root_ref !== null) artifactRef(item.root_ref, `${path}.root_ref`, true);
  const asOf = item.as_of !== undefined && item.as_of !== null ? awareInstant(item.as_of, `${path}.as_of`) : null;
  const recordedAsOf = item.recorded_as_of !== undefined && item.recorded_as_of !== null
    ? exactUtc(item.recorded_as_of, `${path}.recorded_as_of`)
    : null;
  const token = item.snapshot_token !== undefined && item.snapshot_token !== null
    ? snapshotToken(item.snapshot_token, `${path}.snapshot_token`)
    : null;
  if ((recordedAsOf === null) !== (token === null)) fail(path, "recorded_as_of and snapshot_token must be supplied together");
  let comparison: GroundedComparisonWindow | null = null;
  if (item.comparison !== undefined && item.comparison !== null) {
    const window = exactRecord(item.comparison, `${path}.comparison`, ["before_as_of", "after_as_of"]);
    const before = awareInstant(window.before_as_of, `${path}.comparison.before_as_of`);
    const after = awareInstant(window.after_as_of, `${path}.comparison.after_as_of`);
    if (compare(before, after) >= 0) fail(`${path}.comparison`, "before_as_of must be earlier than after_as_of");
    comparison = { before_as_of: before, after_as_of: after };
  }
  if (asOf !== null && comparison !== null) fail(path, "comparison and point as_of are mutually exclusive");
  if (token !== null && asOf === null && comparison === null) fail(path, "snapshot replay requires as_of or comparison");
  const upperEffective = comparison?.after_as_of ?? asOf;
  if (upperEffective && recordedAsOf && compare(upperEffective, recordedAsOf) > 0) {
    fail(path, "effective time cannot exceed recorded_as_of");
  }
}

export function normalizeGroundedProvenanceRequest(
  request: GroundedProvenanceQuestionRequest,
): GroundedProvenanceQuestionRequest {
  validateGroundedProvenanceRequest(request);
  return {
    project_id: request.project_id.toLowerCase(),
    question: request.question,
    ...(request.root_ref ? { root_ref: { entity_kind: "artifact_version", id: request.root_ref.id.toLowerCase() } } : {}),
    ...(request.as_of ? { as_of: normalizeInstantToUtcMicroseconds(request.as_of) } : {}),
    ...(request.recorded_as_of ? { recorded_as_of: request.recorded_as_of } : {}),
    ...(request.snapshot_token ? { snapshot_token: request.snapshot_token } : {}),
    ...(request.comparison ? {
      comparison: {
        before_as_of: normalizeInstantToUtcMicroseconds(request.comparison.before_as_of),
        after_as_of: normalizeInstantToUtcMicroseconds(request.comparison.after_as_of),
      },
    } : {}),
  };
}

function temporalSnapshot(value: unknown, path: string): GroundedTemporalSnapshot {
  const base = record(value, path);
  const kind = literal(base.snapshot_kind, `${path}.snapshot_kind`, ["point", "comparison"] as const);
  if (kind === "point") {
    const item = exactRecord(value, path, ["snapshot_kind", "effective_as_of", "recorded_as_of", "snapshot_token"]);
    const effective = exactUtc(item.effective_as_of, `${path}.effective_as_of`);
    const recorded = exactUtc(item.recorded_as_of, `${path}.recorded_as_of`);
    snapshotToken(item.snapshot_token, `${path}.snapshot_token`);
    if (compare(effective, recorded) > 0) fail(path, "effective_as_of cannot exceed recorded_as_of");
  } else {
    const item = exactRecord(value, path, ["snapshot_kind", "before_as_of", "after_as_of", "recorded_as_of", "snapshot_token"]);
    const before = exactUtc(item.before_as_of, `${path}.before_as_of`);
    const after = exactUtc(item.after_as_of, `${path}.after_as_of`);
    const recorded = exactUtc(item.recorded_as_of, `${path}.recorded_as_of`);
    snapshotToken(item.snapshot_token, `${path}.snapshot_token`);
    if (!(compare(before, after) < 0 && compare(after, recorded) <= 0)) fail(path, "comparison axes must be ordered");
  }
  return value as GroundedTemporalSnapshot;
}

function exactInteger(value: unknown, path: string, expected: number): void {
  if (integer(value, path, expected, expected) !== expected) fail(path, `expected ${expected}`);
}

function pointSourceRequest(
  value: unknown,
  path: string,
  kind: "proof_trace" | "impact_analysis",
): GroundedProofTraceSourceRequest | GroundedImpactSourceRequest {
  const item = exactRecord(value, path, [
    "source_request_kind", "project_id", "root_ref", "as_of", "recorded_as_of", "snapshot_token",
    "max_depth", "max_records", "max_paths", "max_path_steps",
  ]);
  literal(item.source_request_kind, `${path}.source_request_kind`, [kind] as const);
  uuid(item.project_id, `${path}.project_id`);
  artifactRef(item.root_ref, `${path}.root_ref`);
  const effective = exactUtc(item.as_of, `${path}.as_of`);
  const recorded = exactUtc(item.recorded_as_of, `${path}.recorded_as_of`);
  snapshotToken(item.snapshot_token, `${path}.snapshot_token`);
  if (compare(effective, recorded) > 0) fail(path, "effective time cannot exceed recorded_as_of");
  exactInteger(item.max_depth, `${path}.max_depth`, 32);
  exactInteger(item.max_records, `${path}.max_records`, 500);
  exactInteger(item.max_paths, `${path}.max_paths`, 5000);
  exactInteger(item.max_path_steps, `${path}.max_path_steps`, 5000);
  return value as GroundedProofTraceSourceRequest | GroundedImpactSourceRequest;
}

function temporalSourceRequest(value: unknown, path: string): GroundedTemporalCompareSourceRequest {
  const item = exactRecord(value, path, [
    "source_request_kind", "project_id", "root_refs", "before_as_of", "after_as_of", "recorded_as_of",
    "snapshot_token", "direction", "max_depth", "max_records", "max_paths", "max_path_steps",
  ]);
  literal(item.source_request_kind, `${path}.source_request_kind`, ["temporal_compare"] as const);
  uuid(item.project_id, `${path}.project_id`);
  const roots = array(item.root_refs, `${path}.root_refs`, 1, 1);
  artifactRef(roots[0], `${path}.root_refs[0]`);
  const before = exactUtc(item.before_as_of, `${path}.before_as_of`);
  const after = exactUtc(item.after_as_of, `${path}.after_as_of`);
  const recorded = exactUtc(item.recorded_as_of, `${path}.recorded_as_of`);
  snapshotToken(item.snapshot_token, `${path}.snapshot_token`);
  if (!(compare(before, after) < 0 && compare(after, recorded) <= 0)) fail(path, "comparison axes must be ordered");
  literal(item.direction, `${path}.direction`, ["both"] as const);
  exactInteger(item.max_depth, `${path}.max_depth`, 32);
  exactInteger(item.max_records, `${path}.max_records`, 500);
  exactInteger(item.max_paths, `${path}.max_paths`, 5000);
  exactInteger(item.max_path_steps, `${path}.max_path_steps`, 10000);
  return value as GroundedTemporalCompareSourceRequest;
}

function emptyList(value: unknown, path: string): void {
  array(value, path, 0, 0);
}

function humanSourceRequest(value: unknown, path: string): GroundedHumanSourceRequest {
  const item = exactRecord(value, path, [
    "source_request_kind", "project_id", "projection", "root_ref", "as_of", "recorded_as_of", "snapshot_token",
    "record_kinds", "query", "filters", "group_by", "sort", "page_size", "cursor",
  ]);
  literal(item.source_request_kind, `${path}.source_request_kind`, ["human_provenance"] as const);
  uuid(item.project_id, `${path}.project_id`);
  literal(item.projection, `${path}.projection`, ["related"] as const);
  artifactRef(item.root_ref, `${path}.root_ref`);
  const effective = exactUtc(item.as_of, `${path}.as_of`);
  const recorded = exactUtc(item.recorded_as_of, `${path}.recorded_as_of`);
  snapshotToken(item.snapshot_token, `${path}.snapshot_token`);
  if (compare(effective, recorded) > 0) fail(path, "effective time cannot exceed recorded_as_of");
  const recordKinds = array(item.record_kinds, `${path}.record_kinds`, 3, 3).map((entry, index) => (
    literal(entry, `${path}.record_kinds[${index}]`, ["decision", "decision_evidence", "journal_entry"] as const)
  ));
  if (JSON.stringify(recordKinds) !== JSON.stringify(["decision", "decision_evidence", "journal_entry"])) {
    fail(`${path}.record_kinds`, "must retain all canonical human record kinds in order");
  }
  if (item.query !== null) fail(`${path}.query`, "expected null");
  const filters = exactRecord(item.filters, `${path}.filters`, [
    "actor_ids", "decision_statuses", "evidence_types", "entry_types", "sensitivity_tier_ids", "lifecycle_states",
    "effective_from", "effective_through",
  ]);
  for (const field of [
    "actor_ids", "decision_statuses", "evidence_types", "entry_types", "sensitivity_tier_ids", "lifecycle_states",
  ] as const) emptyList(filters[field], `${path}.filters.${field}`);
  if (filters.effective_from !== null) fail(`${path}.filters.effective_from`, "expected null");
  if (filters.effective_through !== null) fail(`${path}.filters.effective_through`, "expected null");
  if (item.group_by !== null) fail(`${path}.group_by`, "expected null");
  literal(item.sort, `${path}.sort`, ["effective_at_desc"] as const);
  exactInteger(item.page_size, `${path}.page_size`, 50);
  if (item.cursor !== null) fail(`${path}.cursor`, "expected null");
  return value as GroundedHumanSourceRequest;
}

function sourceRequest(value: unknown, path: string): GroundedSourceRequest {
  const item = record(value, path);
  const kind = literal(item.source_request_kind, `${path}.source_request_kind`, [
    "proof_trace", "impact_analysis", "temporal_compare", "human_provenance",
  ] as const);
  if (kind === "proof_trace" || kind === "impact_analysis") return pointSourceRequest(value, path, kind);
  return kind === "temporal_compare" ? temporalSourceRequest(value, path) : humanSourceRequest(value, path);
}

function citation(value: unknown, path: string): GroundedCitation {
  const item = exactRecord(value, path, [
    "citation_id", "source_tool", "source_contract_version", "source_semantic_digest",
    "source_locator", "record_kind", "record_id", "secondary_record_id", "effective_at", "recorded_at",
  ]);
  digest(item.citation_id, `${path}.citation_id`);
  const sourceTool = literal(item.source_tool, `${path}.source_tool`, ["proof_trace", "impact_analysis", "temporal_compare", "human_provenance"] as const);
  const sourceContract = identifier(item.source_contract_version, `${path}.source_contract_version`);
  if (sourceContract !== SOURCE_CONTRACTS[sourceTool]) fail(`${path}.source_contract_version`, "must be the canonical contract for this tool");
  digest(item.source_semantic_digest, `${path}.source_semantic_digest`);
  const locator = text(item.source_locator, `${path}.source_locator`, 500);
  const locatorPatterns: Record<GroundedProvenanceSourceTool, RegExp> = {
    proof_trace: /^\/document\/(?:citations\/(?:0|[1-9]\d*)|assertions(?:\/(?:0|[1-9]\d*))?|paths)$/,
    impact_analysis: /^\/document\/(?:root_cause|summary|paths\/(?:0|[1-9]\d*)\/hops\/(?:0|[1-9]\d*)\/(?:input_artifact_version_id|input_binding_id|operation_id|output_binding_id|output_artifact_version_id))$/,
    temporal_compare: /^\/document\/(?:summary|citations\/(?:0|[1-9]\d*))$/,
    human_provenance: /^\/(?:root_ref|items(?:\/(?:0|[1-9]\d*))?)$/,
  };
  if (!locatorPatterns[sourceTool].test(locator)) fail(`${path}.source_locator`, "must be canonical for its source tool");
  const recordKind = identifier(item.record_kind, `${path}.record_kind`);
  const exactLocatorKinds = new Map<string, string>([
    ["proof_trace\u0000/document/assertions", "proof_assertion_set"],
    ["proof_trace\u0000/document/paths", "proof_path_set"],
    ["impact_analysis\u0000/document/root_cause", "artifact_version"],
    ["impact_analysis\u0000/document/summary", "impact_summary"],
    ["temporal_compare\u0000/document/summary", "temporal_compare_summary"],
    ["human_provenance\u0000/root_ref", "artifact_version"],
    ["human_provenance\u0000/items", "human_provenance_page"],
  ]);
  const exactKind = exactLocatorKinds.get(`${sourceTool}\u0000${locator}`);
  if (exactKind && recordKind !== exactKind) fail(`${path}.record_kind`, "must match the canonical locator anchor kind");
  if (sourceTool === "proof_trace" && /^\/document\/citations\/(?:0|[1-9]\d*)$/.test(locator)
    && !PROOF_CITATION_ANCHOR_KINDS.has(recordKind)) {
    fail(`${path}.record_kind`, "proof citation locators require canonical citation anchor kinds");
  }
  if (sourceTool === "proof_trace" && /^\/document\/assertions\/(?:0|[1-9]\d*)$/.test(locator)
    && !PROOF_ASSERTION_ANCHOR_KINDS.has(recordKind)) {
    fail(`${path}.record_kind`, "proof assertion locators require canonical assertion anchor kinds");
  }
  if (sourceTool === "temporal_compare" && /^\/document\/citations\/(?:0|[1-9]\d*)$/.test(locator)
    && !TEMPORAL_CITATION_ANCHOR_KINDS.has(recordKind)) {
    fail(`${path}.record_kind`, "temporal citation locators require canonical citation anchor kinds");
  }
  if (sourceTool === "human_provenance" && /^\/items\/(?:0|[1-9]\d*)$/.test(locator)
    && !HUMAN_ITEM_ANCHOR_KINDS.has(recordKind)) {
    fail(`${path}.record_kind`, "human item locators require canonical item anchor kinds");
  }
  const impactHop = sourceTool === "impact_analysis"
    ? /^\/document\/paths\/(?:0|[1-9]\d*)\/hops\/(?:0|[1-9]\d*)\/(input_artifact_version_id|input_binding_id|operation_id|output_binding_id|output_artifact_version_id)$/.exec(locator)
    : null;
  if (impactHop) {
    const impactHopKinds: Record<string, string> = {
      input_artifact_version_id: "artifact_version",
      input_binding_id: "binding",
      operation_id: "operation_run",
      output_binding_id: "binding",
      output_artifact_version_id: "artifact_version",
    };
    if (recordKind !== impactHopKinds[impactHop[1]!]) {
      fail(`${path}.record_kind`, "impact hop locators require their canonical anchor kinds");
    }
  }
  uuid(item.record_id, `${path}.record_id`);
  const secondaryRecordId = nullableUuid(item.secondary_record_id, `${path}.secondary_record_id`);
  if ((recordKind === "decision_evidence") !== (secondaryRecordId !== null)) {
    fail(`${path}.secondary_record_id`, "must be present exactly for decision_evidence citations");
  }
  const effective = item.effective_at === null ? null : exactUtc(item.effective_at, `${path}.effective_at`);
  const recorded = item.recorded_at === null ? null : exactUtc(item.recorded_at, `${path}.recorded_at`);
  if ((effective === null) !== (recorded === null)) fail(path, "citation temporal axes must be present together");
  if (effective && recorded && compare(effective, recorded) > 0) fail(path, "citation effective_at cannot exceed recorded_at");
  return value as GroundedCitation;
}

function pathStep(value: unknown, path: string): GroundedPathStep {
  const base = record(value, path);
  const kind = literal(base.step_kind, `${path}.step_kind`, ["citation", "operation", "supersession"] as const);
  if (kind === "citation") {
    const item = exactRecord(value, path, ["step_kind", "ordinal", "role", "citation_ids"]);
    integer(item.ordinal, `${path}.ordinal`, 0, MAX_PATH_STEPS - 1);
    identifier(item.role, `${path}.role`);
    digestList(item.citation_ids, `${path}.citation_ids`, 20, 1);
  } else if (kind === "operation") {
    const item = exactRecord(value, path, [
      "step_kind", "ordinal", "from_artifact_version_id", "input_binding_id", "operation_id",
      "output_binding_id", "to_artifact_version_id", "citation_ids",
    ]);
    integer(item.ordinal, `${path}.ordinal`, 0, MAX_PATH_STEPS - 1);
    for (const field of ["from_artifact_version_id", "input_binding_id", "operation_id", "output_binding_id", "to_artifact_version_id"] as const) {
      uuid(item[field], `${path}.${field}`);
    }
    digestList(item.citation_ids, `${path}.citation_ids`, 5, 1);
  } else {
    const item = exactRecord(value, path, [
      "step_kind", "ordinal", "from_artifact_version_id", "supersession_id", "to_artifact_version_id", "citation_ids",
    ]);
    integer(item.ordinal, `${path}.ordinal`, 0, MAX_PATH_STEPS - 1);
    for (const field of ["from_artifact_version_id", "supersession_id", "to_artifact_version_id"] as const) {
      uuid(item[field], `${path}.${field}`);
    }
    digestList(item.citation_ids, `${path}.citation_ids`, 3, 1);
  }
  return value as GroundedPathStep;
}

function groundedPath(value: unknown, path: string): GroundedPath {
  const item = exactRecord(value, path, ["path_id", "source_tool", "source_path_id", "steps", "terminal_kind", "terminal_reason"]);
  digest(item.path_id, `${path}.path_id`);
  literal(item.source_tool, `${path}.source_tool`, ["proof_trace", "impact_analysis", "temporal_compare"] as const);
  digest(item.source_path_id, `${path}.source_path_id`);
  const steps = array(item.steps, `${path}.steps`, MAX_PATH_STEPS, 1).map((entry, index) => pathStep(entry, `${path}.steps[${index}]`));
  steps.forEach((step, index) => { if (step.ordinal !== index) fail(`${path}.steps[${index}].ordinal`, "must be contiguous"); });
  identifier(item.terminal_kind, `${path}.terminal_kind`);
  identifier(item.terminal_reason, `${path}.terminal_reason`);
  return value as GroundedPath;
}

function statement(value: unknown, path: string): GroundedStatement {
  const item = exactRecord(value, path, ["statement_id", "statement_kind", "text", "citation_ids"]);
  const id = text(item.statement_id, `${path}.statement_id`, 11);
  if (!/^statement:[0-7]$/.test(id)) fail(`${path}.statement_id`, "expected a bounded statement ID");
  literal(item.statement_kind, `${path}.statement_kind`, ["conclusion", "scope", "completeness", "decision_context", "no_match", "clarification"] as const);
  const statementText = text(item.text, `${path}.text`, 1000);
  if (!STATEMENT_TEMPLATES.some((template) => template.test(statementText))) {
    fail(`${path}.text`, "must use one canonical fixed statement template");
  }
  digestList(item.citation_ids, `${path}.citation_ids`, 100);
  return value as GroundedStatement;
}

function toolCall(value: unknown, path: string): GroundedToolCall {
  const item = exactRecord(value, path, [
    "ordinal", "tool", "source_contract_version", "source_semantic_digest", "source_request", "evidence_status", "visible_record_count",
  ]);
  integer(item.ordinal, `${path}.ordinal`, 0, MAX_TOOL_CALLS - 1);
  const tool = literal(item.tool, `${path}.tool`, ["proof_trace", "impact_analysis", "temporal_compare", "human_provenance"] as const);
  const sourceContract = identifier(item.source_contract_version, `${path}.source_contract_version`);
  if (sourceContract !== SOURCE_CONTRACTS[tool]) fail(`${path}.source_contract_version`, "must be the canonical contract for this tool");
  digest(item.source_semantic_digest, `${path}.source_semantic_digest`);
  const request = sourceRequest(item.source_request, `${path}.source_request`);
  if (request.source_request_kind !== tool) fail(`${path}.source_request`, "must retain the exact request for this tool");
  literal(item.evidence_status, `${path}.evidence_status`, ["complete", "incomplete", "redacted"] as const);
  integer(item.visible_record_count, `${path}.visible_record_count`, 0, Number.MAX_SAFE_INTEGER);
  return value as GroundedToolCall;
}

function requirement(value: unknown, path: string): GroundedRequirement {
  const item = exactRecord(value, path, ["code", "message"]);
  literal(item.code, `${path}.code`, ["artifact_version_root", "comparison_window", "clarify_intent"] as const);
  text(item.message, `${path}.message`, 300);
  return value as GroundedRequirement;
}

function warning(value: unknown, path: string): GroundedWarning {
  const item = exactRecord(value, path, ["code"]);
  literal(item.code, `${path}.code`, [
    "proof_incomplete", "impact_incomplete", "temporal_history_incomplete", "restricted_evidence_omitted",
    "human_history_incomplete", "citation_limit_reached", "path_limit_reached", "no_authorized_human_evidence",
    "human_evidence_page_truncated",
  ] as const);
  return value as GroundedWarning;
}

function openAction(value: unknown, path: string): GroundedOpenAction {
  const item = exactRecord(value, path, ["action_id", "target_view", "label", "tool_call_ordinal", "root_ref", "highlight_citation_id"]);
  const id = text(item.action_id, `${path}.action_id`, 8);
  if (!/^action:[0-3]$/.test(id)) fail(`${path}.action_id`, "expected a bounded action ID");
  const targetView = literal(item.target_view, `${path}.target_view`, ["proof_trace", "impact", "temporal_compare", "human_provenance"] as const);
  const label = text(item.label, `${path}.label`, 100);
  const expectedLabel = {
    proof_trace: "Open Proof Trace",
    impact: "Open Impact",
    temporal_compare: "Open Time Compare",
    human_provenance: "Open Human Provenance",
  } as const;
  if (label !== expectedLabel[targetView]) fail(`${path}.label`, "must use the canonical source-action label");
  integer(item.tool_call_ordinal, `${path}.tool_call_ordinal`, 0, MAX_TOOL_CALLS - 1);
  artifactRef(item.root_ref, `${path}.root_ref`);
  if (item.highlight_citation_id !== null) digest(item.highlight_citation_id, `${path}.highlight_citation_id`);
  return value as GroundedOpenAction;
}

function exactStatementCitation(
  value: GroundedStatement,
  citationsById: ReadonlyMap<string, GroundedCitation>,
  path: string,
): GroundedCitation {
  if (value.citation_ids.length !== 1) fail(`${path}.citation_ids`, "must contain exactly one canonical source citation");
  const resolved = citationsById.get(value.citation_ids[0]!);
  if (!resolved) fail(`${path}.citation_ids`, "must resolve within citations");
  return resolved;
}

function citationMatchesTool(value: GroundedCitation, tool: GroundedToolCall): boolean {
  return value.source_tool === tool.tool
    && value.source_contract_version === tool.source_contract_version
    && value.source_semantic_digest === tool.source_semantic_digest;
}

function validateEvidenceStatementSupport(
  intent: GroundedProvenanceIntent,
  statements: readonly GroundedStatement[],
  citationsById: ReadonlyMap<string, GroundedCitation>,
  tools: readonly GroundedToolCall[],
  root: GroundedArtifactVersionRef,
  path: string,
): void {
  const proof = tools.find((tool) => tool.tool === "proof_trace");
  const impact = tools.find((tool) => tool.tool === "impact_analysis");
  const temporal = tools.find((tool) => tool.tool === "temporal_compare");
  const human = tools.find((tool) => tool.tool === "human_provenance");
  const kinds = statements.map((statement) => statement.statement_kind);

  if (intent === "why_valid") {
    const expected = statements.length === 2
      ? ["conclusion", "scope"]
      : ["conclusion", "scope", "decision_context"];
    if ((statements.length !== 2 && statements.length !== 3) || JSON.stringify(kinds) !== JSON.stringify(expected)) {
      fail(`${path}.statements`, "why_valid requires conclusion, scope, and optional decision_context statements in order");
    }
    if (!proof || !human) fail(`${path}.tool_calls`, "why_valid requires its proof and human sources");
    const conclusion = exactStatementCitation(statements[0]!, citationsById, `${path}.statements[0]`);
    const rootLifecycleAssertion = /^\/document\/assertions\/(?:0|[1-9]\d*)$/.test(conclusion.source_locator)
      && conclusion.record_kind === "lifecycle_validity_assertion";
    const rootAssertionSet = conclusion.source_locator === "/document/assertions"
      && conclusion.record_kind === "proof_assertion_set";
    if (!citationMatchesTool(conclusion, proof)
      || conclusion.record_id !== root.id
      || (!rootLifecycleAssertion && !rootAssertionSet)) {
      fail(`${path}.statements[0].citation_ids`, "why_valid conclusion must cite the root lifecycle assertion or canonical assertion set");
    }
    if (!WHY_CONCLUSION_TEMPLATE.test(statements[0]!.text)) {
      fail(`${path}.statements[0].text`, "must use the canonical why_valid conclusion template");
    }
    const scope = exactStatementCitation(statements[1]!, citationsById, `${path}.statements[1]`);
    if (!citationMatchesTool(scope, proof)
      || scope.source_locator !== "/document/paths"
      || scope.record_kind !== "proof_path_set"
      || scope.record_id !== root.id) {
      fail(`${path}.statements[1].citation_ids`, "why_valid scope must cite the root proof path set");
    }
    if (!WHY_SCOPE_TEMPLATE.test(statements[1]!.text)) {
      fail(`${path}.statements[1].text`, "must use the canonical why_valid scope template");
    }
    if (statements.length === 3) {
      const context = exactStatementCitation(statements[2]!, citationsById, `${path}.statements[2]`);
      if (!citationMatchesTool(context, human)
        || context.source_locator !== "/items"
        || context.record_kind !== "human_provenance_page"
        || context.record_id !== root.id) {
        fail(`${path}.statements[2].citation_ids`, "why_valid decision context must cite the authorized human page");
      }
      if (!WHY_CONTEXT_TEMPLATE.test(statements[2]!.text)) {
        fail(`${path}.statements[2].text`, "must use the canonical why_valid decision context template");
      }
    }
    return;
  }

  if (intent === "downstream_invalidation" || intent === "what_changed") {
    if (JSON.stringify(kinds) !== JSON.stringify(["conclusion", "scope"])) {
      fail(`${path}.statements`, `${intent} requires exactly conclusion and scope statements in order`);
    }
    const tool = intent === "downstream_invalidation" ? impact : temporal;
    const expectedLocator = "/document/summary";
    const expectedKind = intent === "downstream_invalidation" ? "impact_summary" : "temporal_compare_summary";
    if (!tool) fail(`${path}.tool_calls`, `${intent} requires its canonical source`);
    for (const [index, statement] of statements.entries()) {
      const support = exactStatementCitation(statement, citationsById, `${path}.statements[${index}]`);
      if (!citationMatchesTool(support, tool)
        || support.source_locator !== expectedLocator
        || support.record_kind !== expectedKind
        || support.record_id !== root.id) {
        fail(`${path}.statements[${index}].citation_ids`, `${intent} statements must cite the root source summary`);
      }
    }
    const templates = intent === "downstream_invalidation"
      ? [IMPACT_CONCLUSION_TEMPLATE, IMPACT_SCOPE_TEMPLATE]
      : [TEMPORAL_CONCLUSION_TEMPLATE, TEMPORAL_SCOPE_TEMPLATE];
    statements.forEach((statement, index) => {
      if (!templates[index]!.test(statement.text)) {
        fail(`${path}.statements[${index}].text`, `must use the canonical ${intent} statement template`);
      }
    });
    return;
  }

  if (JSON.stringify(kinds) !== JSON.stringify(["decision_context"])) {
    fail(`${path}.statements`, "decision_basis requires exactly one decision_context statement");
  }
  if (!human) fail(`${path}.tool_calls`, "decision_basis requires its canonical human source");
  const context = exactStatementCitation(statements[0]!, citationsById, `${path}.statements[0]`);
  if (!citationMatchesTool(context, human)
    || context.source_locator !== "/items"
    || context.record_kind !== "human_provenance_page"
    || context.record_id !== root.id) {
    fail(`${path}.statements[0].citation_ids`, "decision_basis must cite the authorized human page");
  }
  if (!DECISION_CONTEXT_TEMPLATE.test(statements[0]!.text)) {
    fail(`${path}.statements[0].text`, "must use the canonical decision_basis statement template");
  }
}

function validateImpactOperationSupport(
  step: GroundedOperationStep,
  citations: readonly GroundedCitation[],
  path: string,
): void {
  if (citations.length !== 5) fail(`${path}.citation_ids`, "impact operation steps require exactly five hop-field citations");
  const expected = {
    input_artifact_version_id: { kind: "artifact_version", id: step.from_artifact_version_id },
    input_binding_id: { kind: "binding", id: step.input_binding_id },
    operation_id: { kind: "operation_run", id: step.operation_id },
    output_binding_id: { kind: "binding", id: step.output_binding_id },
    output_artifact_version_id: { kind: "artifact_version", id: step.to_artifact_version_id },
  } as const;
  const located = new Map<string, GroundedCitation>();
  let commonHop: string | null = null;
  for (const citation of citations) {
    const match = /^(\/document\/paths\/(?:0|[1-9]\d*)\/hops\/((?:0|[1-9]\d*)))\/(input_artifact_version_id|input_binding_id|operation_id|output_binding_id|output_artifact_version_id)$/.exec(citation.source_locator);
    if (!match || Number(match[2]) !== step.ordinal || (commonHop !== null && commonHop !== match[1]) || located.has(match?.[3] ?? "")) {
      fail(`${path}.citation_ids`, "impact operation citations must identify one exact source path hop");
    }
    commonHop = match[1]!;
    located.set(match[3]!, citation);
  }
  for (const [field, identity] of Object.entries(expected)) {
    const citation = located.get(field);
    if (!citation || citation.record_kind !== identity.kind || citation.record_id !== identity.id) {
      fail(`${path}.citation_ids`, `impact operation citation ${field} must match the step identity`);
    }
  }
}

function validateTemporalStepSupport(
  step: GroundedPathStep,
  citations: readonly GroundedCitation[],
  path: string,
): void {
  if (step.step_kind === "operation") {
    const actual = new Set(citations.map((citation) => `${citation.record_kind}\u0000${citation.record_id}`));
    const required = new Set([
      `binding\u0000${step.input_binding_id}`,
      `operation_run\u0000${step.operation_id}`,
      `binding\u0000${step.output_binding_id}`,
    ]);
    if (actual.size !== required.size || [...required].some((identity) => !actual.has(identity))) {
      fail(`${path}.citation_ids`, "temporal operation citations must exactly match the native step");
    }
    return;
  }
  if (step.step_kind === "supersession") {
    const actual = new Set(citations.map((citation) => `${citation.record_kind}\u0000${citation.record_id}`));
    if (actual.size !== 1 || !actual.has(`supersession\u0000${step.supersession_id}`)) {
      fail(`${path}.citation_ids`, "temporal supersession must cite its exact native supersession identity");
    }
    return;
  }
  if (step.role === "temporal_delta") {
    if (!citations.every((citation) => /^\/document\/citations\/(?:0|[1-9]\d*)$/.test(citation.source_locator))) {
      fail(`${path}.citation_ids`, "temporal_delta may cite only native temporal citations");
    }
    return;
  }
  if (step.role !== "temporal_observation"
    || citations.length !== 1
    || citations[0]!.source_locator !== "/document/summary"
    || citations[0]!.record_kind !== "temporal_compare_summary") {
    fail(`${path}.citation_ids`, "temporal observation steps must cite only the comparison summary");
  }
}

function validateActionHighlight(
  citation: GroundedCitation | undefined,
  tool: GroundedToolCall | undefined,
  root: GroundedArtifactVersionRef | null,
  path: string,
): void {
  if (!citation || !tool || !root) fail(`${path}.highlight_citation_id`, "evidence actions require one resolvable canonical highlight");
  const valid = tool.tool === "proof_trace"
    ? /^\/document\/citations\/(?:0|[1-9]\d*)$/.test(citation.source_locator)
      && citation.record_kind === "artifact_version" && citation.record_id === root.id
    : tool.tool === "impact_analysis"
      ? citation.source_locator === "/document/root_cause"
        && citation.record_kind === "artifact_version" && citation.record_id === root.id
      : tool.tool === "temporal_compare"
        ? citation.source_locator === "/document/summary"
          && citation.record_kind === "temporal_compare_summary" && citation.record_id === root.id
        : (citation.source_locator === "/items" || /^\/items\/(?:0|[1-9]\d*)$/.test(citation.source_locator));
  if (!valid) fail(`${path}.highlight_citation_id`, "must identify the canonical highlight for its target source view");
}

function document(value: unknown, path: string): GroundedProvenanceDocument {
  const item = exactRecord(value, path, [
    "contract_version", "resource_limits_version", "project_id", "question", "normalized_question", "intent", "status",
    "root_ref", "temporal_snapshot", "statements", "citations", "paths", "tool_calls", "open_actions", "requirements", "warnings",
  ]);
  literal(item.contract_version, `${path}.contract_version`, [GROUNDED_PROVENANCE_CONTRACT_VERSION]);
  literal(item.resource_limits_version, `${path}.resource_limits_version`, [GROUNDED_PROVENANCE_RESOURCE_LIMITS_VERSION]);
  uuid(item.project_id, `${path}.project_id`);
  const question = text(item.question, `${path}.question`, MAX_QUESTION_LENGTH);
  if (question !== question.trim()) fail(`${path}.question`, "must be trimmed");
  const normalized = text(item.normalized_question, `${path}.normalized_question`, MAX_QUESTION_LENGTH);
  if (normalized !== normalizeGroundedQuestion(question, `${path}.question`)) fail(`${path}.normalized_question`, "must be derived from question");
  const intent = item.intent === null ? null : literal(item.intent, `${path}.intent`, ["why_valid", "what_changed", "downstream_invalidation", "decision_basis"] as const);
  const status = literal(item.status, `${path}.status`, ["answered", "incomplete", "redacted", "no_match", "ambiguous", "needs_context"] as const);
  const root = nullableArtifactRef(item.root_ref, `${path}.root_ref`);
  const snapshot = item.temporal_snapshot === null ? null : temporalSnapshot(item.temporal_snapshot, `${path}.temporal_snapshot`);
  const statements = array(item.statements, `${path}.statements`, MAX_STATEMENTS, 1).map((entry, index) => statement(entry, `${path}.statements[${index}]`));
  statements.forEach((entry, index) => { if (entry.statement_id !== `statement:${index}`) fail(`${path}.statements[${index}].statement_id`, "must be contiguous"); });
  const citations = array(item.citations, `${path}.citations`, MAX_CITATIONS).map((entry, index) => citation(entry, `${path}.citations[${index}]`));
  const citationIds = citations.map((entry) => entry.citation_id);
  if (!sortedUnique(citationIds)) fail(`${path}.citations`, "must be unique and sorted by citation_id");
  const citationSet = new Set(citationIds);
  const citationsById = new Map(citations.map((entry) => [entry.citation_id, entry]));
  const paths = array(item.paths, `${path}.paths`, MAX_PATHS).map((entry, index) => groundedPath(entry, `${path}.paths[${index}]`));
  const pathIds = paths.map((entry) => entry.path_id);
  if (!sortedUnique(pathIds)) fail(`${path}.paths`, "path IDs must be unique and sorted");
  if (paths.reduce((total, entry) => total + entry.steps.length, 0) > MAX_PATH_STEPS) {
    fail(`${path}.paths`, "must not exceed the aggregate path-step limit");
  }
  const tools = array(item.tool_calls, `${path}.tool_calls`, MAX_TOOL_CALLS).map((entry, index) => toolCall(entry, `${path}.tool_calls[${index}]`));
  tools.forEach((entry, index) => { if (entry.ordinal !== index) fail(`${path}.tool_calls[${index}].ordinal`, "must be contiguous"); });
  const toolIdentities = new Set(tools.map((entry) => JSON.stringify([entry.tool, entry.source_contract_version, entry.source_semantic_digest])));
  if (toolIdentities.size !== tools.length) fail(`${path}.tool_calls`, "tool calls must be unique");
  const actions = array(item.open_actions, `${path}.open_actions`, 4).map((entry, index) => openAction(entry, `${path}.open_actions[${index}]`));
  actions.forEach((entry, index) => { if (entry.action_id !== `action:${index}`) fail(`${path}.open_actions[${index}].action_id`, "must be contiguous"); });
  const requirements = array(item.requirements, `${path}.requirements`, 3).map((entry, index) => requirement(entry, `${path}.requirements[${index}]`));
  const warnings = array(item.warnings, `${path}.warnings`, 20).map((entry, index) => warning(entry, `${path}.warnings[${index}]`));
  const requirementCodes = requirements.map((entry) => entry.code);
  if (!sortedUnique(requirementCodes)) fail(`${path}.requirements`, "must be unique and sorted by code");
  const warningCodes = warnings.map((entry) => entry.code);
  if (!sortedUnique(warningCodes)) fail(`${path}.warnings`, "must be unique and sorted by code");

  for (const [index, tool] of tools.entries()) {
    const source = tool.source_request;
    const sourcePath = `${path}.tool_calls[${index}].source_request`;
    if (source.project_id !== item.project_id) fail(`${sourcePath}.project_id`, "must retain the answer project");
    if (source.source_request_kind === "temporal_compare") {
      if (snapshot?.snapshot_kind !== "comparison") fail(sourcePath, "temporal source request requires the answer comparison snapshot");
      if (root === null || !sameArtifactRef(source.root_refs[0], root)) fail(`${sourcePath}.root_refs`, "must retain the answer root");
      if (snapshot && snapshot.snapshot_kind === "comparison" && (
        compare(source.before_as_of, snapshot.before_as_of) !== 0
        || compare(source.after_as_of, snapshot.after_as_of) !== 0
        || source.recorded_as_of !== snapshot.recorded_as_of
        || source.snapshot_token !== snapshot.snapshot_token
      )) fail(sourcePath, "must retain the answer comparison cut");
    } else {
      if (snapshot?.snapshot_kind !== "point") fail(sourcePath, "point source request requires the answer point snapshot");
      if (root === null || !sameArtifactRef(source.root_ref, root)) fail(`${sourcePath}.root_ref`, "must retain the answer root");
      if (snapshot && snapshot.snapshot_kind === "point" && (
        source.as_of !== snapshot.effective_as_of
        || source.recorded_as_of !== snapshot.recorded_as_of
        || source.snapshot_token !== snapshot.snapshot_token
      )) fail(sourcePath, "must retain the answer point cut");
    }
  }

  for (const [index, entry] of citations.entries()) {
    if (!toolIdentities.has(JSON.stringify([entry.source_tool, entry.source_contract_version, entry.source_semantic_digest]))) {
      fail(`${path}.citations[${index}]`, "must resolve into a declared exact tool result");
    }
  }
  for (const [index, entry] of statements.entries()) {
    if (!entry.citation_ids.every((citationId) => citationSet.has(citationId))) fail(`${path}.statements[${index}].citation_ids`, "must resolve within citations");
  }
  for (const [pathIndex, entry] of paths.entries()) {
    const pathTool = tools.find((tool) => tool.tool === entry.source_tool);
    if (!pathTool) {
      fail(`${path}.paths[${pathIndex}].source_tool`, "must be a declared tool result");
    }
    for (const [stepIndex, step] of entry.steps.entries()) {
      if (!step.citation_ids.every((citationId) => citationSet.has(citationId))) {
        fail(`${path}.paths[${pathIndex}].steps[${stepIndex}].citation_ids`, "must resolve within citations");
      }
      const stepPath = `${path}.paths[${pathIndex}].steps[${stepIndex}]`;
      const stepCitations = step.citation_ids.map((citationId) => citationsById.get(citationId)!);
      if (!stepCitations.every((citation) => citationMatchesTool(citation, pathTool))) {
        fail(`${stepPath}.citation_ids`, "must belong to the path's exact source result");
      }
      if (entry.source_tool === "proof_trace" && step.step_kind !== "citation") {
        fail(stepPath, "proof paths may contain only canonical citation steps");
      }
      if (entry.source_tool === "proof_trace"
        && !stepCitations.every((citation) => /^\/document\/citations\/(?:0|[1-9]\d*)$/.test(citation.source_locator))) {
        fail(`${stepPath}.citation_ids`, "proof path steps require native proof citations");
      }
      if (entry.source_tool === "impact_analysis") {
        if (step.step_kind !== "operation") fail(stepPath, "impact paths may contain only typed operation hops");
        validateImpactOperationSupport(step, stepCitations, stepPath);
      } else if (entry.source_tool === "temporal_compare") {
        validateTemporalStepSupport(step, stepCitations, stepPath);
      }
    }
  }
  for (const [index, action] of actions.entries()) {
    if (root !== null && (action.root_ref.entity_kind !== root.entity_kind || action.root_ref.id !== root.id)) {
      fail(`${path}.open_actions[${index}].root_ref`, "must retain the answer root");
    }
    if (action.highlight_citation_id !== null && !citationSet.has(action.highlight_citation_id)) {
      fail(`${path}.open_actions[${index}].highlight_citation_id`, "must resolve within citations");
    }
    const actionTool = tools[action.tool_call_ordinal];
    if (!actionTool) fail(`${path}.open_actions[${index}].tool_call_ordinal`, "must resolve to a declared source tool");
    const expectedView = {
      proof_trace: "proof_trace",
      impact_analysis: "impact",
      temporal_compare: "temporal_compare",
      human_provenance: "human_provenance",
    } as const;
    if (actionTool && action.target_view !== expectedView[actionTool.tool]) {
      fail(`${path}.open_actions[${index}].target_view`, "must replay its declared source tool");
    }
    const highlighted = action.highlight_citation_id === null
      ? undefined
      : citationsById.get(action.highlight_citation_id);
    if (actionTool && highlighted) {
      if (!highlighted
        || highlighted.source_tool !== actionTool.tool
        || highlighted.source_semantic_digest !== actionTool.source_semantic_digest) {
        fail(`${path}.open_actions[${index}].highlight_citation_id`, "must belong to the selected exact source tool");
      }
    }
    validateActionHighlight(highlighted, actionTool, root, `${path}.open_actions[${index}]`);
  }

  const evidenceStatus = status === "answered" || status === "incomplete" || status === "redacted";
  if (evidenceStatus) {
    if (intent === null || root === null || snapshot === null || tools.length === 0 || citations.length === 0 || actions.length === 0 || requirements.length !== 0) {
      fail(path, "evidence answers require a closed tool-backed shape");
    }
    const actualTools = tools.map((entry) => entry.tool);
    const expectedTools: Record<GroundedProvenanceIntent, GroundedProvenanceSourceTool[]> = {
      why_valid: ["proof_trace", "human_provenance"],
      what_changed: ["temporal_compare"],
      downstream_invalidation: ["impact_analysis"],
      decision_basis: ["human_provenance"],
    };
    const canonicalPlan = expectedTools[intent];
    if (JSON.stringify(actualTools) !== JSON.stringify(canonicalPlan)) {
      fail(`${path}.tool_calls`, "must equal the canonical tool plan for the answer intent");
    }
    if (statements.some((statement) => statement.citation_ids.length === 0)) {
      fail(`${path}.statements`, "every evidence statement requires canonical source support");
    }
    validateEvidenceStatementSupport(intent, statements, citationsById, tools, root, path);
    if (actions.length !== 1) fail(`${path}.open_actions`, "evidence answers require exactly one source action");
    if ((intent === "what_changed") !== (snapshot?.snapshot_kind === "comparison")) {
      fail(`${path}.temporal_snapshot`, "only what_changed may use a comparison snapshot");
    }
    const expectedView: Record<GroundedProvenanceIntent, GroundedOpenAction["target_view"]> = {
      why_valid: "proof_trace",
      what_changed: "temporal_compare",
      downstream_invalidation: "impact",
      decision_basis: "human_provenance",
    };
    if (!actions.some((entry) => entry.target_view === expectedView[intent])) {
      fail(`${path}.open_actions`, "must include the canonical view for the answer intent");
    }
    const evidenceStatuses = new Set(tools.map((entry) => entry.evidence_status));
    const derivedStatus: GroundedProvenanceStatus = evidenceStatuses.has("redacted")
      ? "redacted"
      : evidenceStatuses.has("incomplete")
        ? "incomplete"
        : "answered";
    if (status !== derivedStatus) fail(`${path}.status`, "must derive from tool evidence completeness");
  } else if (status === "needs_context") {
    if (intent === null
      || requirements.length === 0
      || tools.length !== 0
      || snapshot !== null
      || citations.length !== 0
      || paths.length !== 0
      || actions.length !== 0
      || warnings.length !== 0) {
      fail(path, "needs_context requires an intent and no tool evidence");
    }
  } else if (status === "ambiguous") {
    if (intent !== null
      || root !== null
      || snapshot !== null
      || requirements.length === 0
      || tools.length !== 0
      || citations.length !== 0
      || paths.length !== 0
      || actions.length !== 0
      || warnings.length !== 0) {
      fail(path, "ambiguous answers require clarification only");
    }
  } else if (status === "no_match" && (
    intent !== null
    || root !== null
    || snapshot !== null
    || requirements.length !== 0
    || tools.length !== 0
    || citations.length !== 0
    || paths.length !== 0
    || actions.length !== 0
    || warnings.length !== 0
  )) {
    fail(path, "no_match cannot claim context, navigation or evidence");
  }
  if (status === "redacted" && !warnings.some((entry) => entry.code === "restricted_evidence_omitted")) {
    fail(`${path}.warnings`, "redacted status requires restricted_evidence_omitted");
  }
  return value as GroundedProvenanceDocument;
}

export function parseGroundedProvenanceEnvelope(value: unknown): GroundedProvenanceEnvelope {
  const item = exactRecord(value, "$", ["schema_version", "request_id", "data", "page", "provenance", "warnings"]);
  literal(item.schema_version, "$.schema_version", ["thread.api.v1"]);
  uuid(item.request_id, "$.request_id");
  if (item.page !== null) fail("$.page", "expected null for a non-paginated answer");
  const data = exactRecord(item.data, "$.data", ["document", "semantic_identity"]);
  document(data.document, "$.data.document");
  const identity = exactRecord(data.semantic_identity, "$.data.semantic_identity", ["digest", "canonicalizer", "canonicalizer_version", "byte_length", "media_type"]);
  digest(identity.digest, "$.data.semantic_identity.digest");
  literal(identity.canonicalizer, "$.data.semantic_identity.canonicalizer", ["rfc8785"]);
  literal(identity.canonicalizer_version, "$.data.semantic_identity.canonicalizer_version", ["1"]);
  integer(identity.byte_length, "$.data.semantic_identity.byte_length", 1, MAX_DOCUMENT_CANONICAL_BYTES);
  literal(identity.media_type, "$.data.semantic_identity.media_type", [GROUNDED_PROVENANCE_JSON_MEDIA_TYPE]);
  const provenance = exactRecord(item.provenance, "$.provenance", ["source", "projection_version", "schema_migration"]);
  literal(provenance.source, "$.provenance.source", ["thread_core"]);
  literal(provenance.projection_version, "$.provenance.projection_version", [GROUNDED_PROVENANCE_CONTRACT_VERSION]);
  const migration = text(provenance.schema_migration, "$.provenance.schema_migration", 14, 14);
  if (!/^\d{14}$/.test(migration) || migration < GROUNDED_PROVENANCE_MIN_SCHEMA_MIGRATION) {
    fail("$.provenance.schema_migration", `expected a migration marker at or after ${GROUNDED_PROVENANCE_MIN_SCHEMA_MIGRATION}`);
  }
  if (array(item.warnings, "$.warnings", 0).length !== 0) fail("$.warnings", "expected no transport warnings");
  return value as GroundedProvenanceEnvelope;
}

function canonicalJson(value: unknown, path = "$canonical"): string {
  if (value === null || typeof value === "boolean" || typeof value === "string") return JSON.stringify(value);
  if (typeof value === "number") {
    if (!Number.isFinite(value) || (Number.isInteger(value) && !Number.isSafeInteger(value))) {
      fail(path, "expected a finite RFC 8785 number without lossy integer precision");
    }
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) return `[${value.map((entry, index) => canonicalJson(entry, `${path}[${index}]`)).join(",")}]`;
  const item = record(value, path);
  return `{${Object.keys(item).sort().map((key) => `${JSON.stringify(key)}:${canonicalJson(item[key], `${path}.${key}`)}`).join(",")}}`;
}

async function sha256(value: unknown): Promise<string> {
  const bytes = new TextEncoder().encode(canonicalJson(value));
  const hash = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return `sha256:${[...new Uint8Array(hash)].map((byte) => byte.toString(16).padStart(2, "0")).join("")}`;
}

/** Compute the same RFC 8785/SHA-256 identity used by grounded source tools. */
export function canonicalGroundedSourceDigest(value: unknown): Promise<string> {
  return sha256(value);
}

function throwIfAborted(signal?: AbortSignal): void {
  if (!signal?.aborted) return;
  if (typeof signal.throwIfAborted === "function") signal.throwIfAborted();
  throw new DOMException("Grounded provenance verification was aborted.", "AbortError");
}

export async function verifyGroundedProvenanceIdentities(
  envelope: GroundedProvenanceEnvelope,
  options: { signal?: AbortSignal } = {},
): Promise<void> {
  const { document: answer, semantic_identity: identity } = envelope.data;
  throwIfAborted(options.signal);
  for (const [index, entry] of answer.citations.entries()) {
    const { citation_id: expected, ...payload } = entry;
    if (await sha256(payload) !== expected) fail(`$.data.document.citations[${index}].citation_id`, "must identify the exact citation locator");
    throwIfAborted(options.signal);
  }
  for (const [index, entry] of answer.paths.entries()) {
    const { path_id: expected, ...payload } = entry;
    if (await sha256(payload) !== expected) fail(`$.data.document.paths[${index}].path_id`, "must identify the exact grounded path");
    throwIfAborted(options.signal);
  }
  const bytes = new TextEncoder().encode(canonicalJson(answer));
  if (bytes.byteLength !== identity.byte_length) fail("$.data.semantic_identity.byte_length", "must equal the RFC 8785 document byte length");
  if (await sha256(answer) !== identity.digest) fail("$.data.semantic_identity.digest", "must equal SHA-256 of the grounded document");
  throwIfAborted(options.signal);
}

function validateResponseBinding(
  envelope: GroundedProvenanceEnvelope,
  request: GroundedProvenanceQuestionRequest,
): void {
  const answer = envelope.data.document;
  if (answer.project_id !== request.project_id) fail("$.data.document.project_id", "must equal request.project_id");
  if (answer.question !== request.question) fail("$.data.document.question", "must equal the exact submitted question");
  if (request.root_ref && answer.intent !== null && (
    answer.root_ref === null
    || answer.root_ref.entity_kind !== request.root_ref.entity_kind
    || answer.root_ref.id !== request.root_ref.id
  )) {
    fail("$.data.document.root_ref", "must equal the exact requested ArtifactVersion for a recognized intent");
  }
  const snapshot = answer.temporal_snapshot;
  if (!snapshot) return;
  if (answer.intent === "what_changed" && request.comparison) {
    if (snapshot.snapshot_kind !== "comparison") fail("$.data.document.temporal_snapshot", "must preserve the requested comparison window");
    if (compare(snapshot.before_as_of, request.comparison.before_as_of) !== 0 || compare(snapshot.after_as_of, request.comparison.after_as_of) !== 0) {
      fail("$.data.document.temporal_snapshot", "must preserve the requested comparison window");
    }
  } else if (request.as_of && snapshot.snapshot_kind === "point") {
    if (snapshot.snapshot_kind !== "point" || compare(snapshot.effective_as_of, request.as_of) !== 0) {
      fail("$.data.document.temporal_snapshot", "must preserve the requested effective instant");
    }
  }
  if (request.recorded_as_of && snapshot.recorded_as_of !== request.recorded_as_of) {
    fail("$.data.document.temporal_snapshot.recorded_as_of", "must equal request.recorded_as_of");
  }
  if (request.snapshot_token && snapshot.snapshot_token !== request.snapshot_token) {
    fail("$.data.document.temporal_snapshot.snapshot_token", "must equal request.snapshot_token");
  }
}

export async function fetchGroundedProvenance(
  request: GroundedProvenanceQuestionRequest,
  options: { signal?: AbortSignal } = {},
): Promise<GroundedProvenanceEnvelope> {
  const normalized = normalizeGroundedProvenanceRequest(request);
  const value = await apiClient<unknown>(GROUNDED_PROVENANCE_PATH, {
    method: "POST",
    body: normalized,
    signal: options.signal,
  });
  const envelope = parseGroundedProvenanceEnvelope(value);
  validateResponseBinding(envelope, normalized);
  await verifyGroundedProvenanceIdentities(envelope, options);
  return envelope;
}
